import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { getTableRows } from "@/shared/utils/storedProcedureRows";
import { get } from "lodash-es";
import moment from "moment";

import { WEIGHT_ITEM_CODE } from "@/shared/models/SectionSpec";
import { MILL_2 } from "@/setup/mill-config/mill-config";
import {
	hasInspectionTolerances,
	type InspectionToleranceSource,
} from "../utils/hasInspectionTolerances";
import { groupRowsByInspection } from "./TestShackReportRepository";
import type {
	CurrentShiftMetadata,
	Inspection,
	InspectionValue,
	ProfileInspectionsResult,
	ShiftInspection,
	ShiftInspectionsResult,
	UpsertInspectionPayload,
} from "../models/Inspection";

type WeightFields =
	| "MeasuredLbsPerFoot"
	| "TargetLbsPerFoot"
	| "MinLbsPerFoot"
	| "MaxLbsPerFoot"
	| "WeightStatus";

interface InspectionResponse
	extends Omit<Inspection, "InspectedAt" | WeightFields> {
	InspectedAt: string;
}

interface InspectionReadings {
	values: Record<number, InspectionValue>;
	weight: Pick<Inspection, WeightFields>;
}

interface ProfileSpecApplicabilityResponse {
	ProfileSpecId: number;
	ProfileItemId: number | null;
	Code: string | null;
	SortOrder: number | null;
	Ltl?: number | null;
	Utl?: number | null;
}

const NO_WEIGHT: Pick<Inspection, WeightFields> = {
	MeasuredLbsPerFoot: null,
	TargetLbsPerFoot: null,
	MinLbsPerFoot: null,
	MaxLbsPerFoot: null,
	WeightStatus: null,
};

const groupReadings = (values: InspectionValue[]) => {
	const byInspection = new Map<number, InspectionReadings>();
	for (const v of values) {
		let bucket = byInspection.get(v.ProfileInspectionId);
		if (!bucket) {
			bucket = { values: {}, weight: NO_WEIGHT };
			byInspection.set(v.ProfileInspectionId, bucket);
		}
		if (v.Code === WEIGHT_ITEM_CODE) {
			bucket.weight = {
				MeasuredLbsPerFoot: v.NumericValue,
				TargetLbsPerFoot: v.Nominal,
				MinLbsPerFoot: v.Ltl,
				MaxLbsPerFoot: v.Utl,
				WeightStatus: v.Status,
			};
		} else {
			bucket.values[v.ProfileItemId] = v;
		}
	}
	return byInspection;
};

interface CurrentShiftMetadataResponse
	extends Omit<CurrentShiftMetadata, "Start" | "End"> {
	Start: string;
	End: string;
}

interface ShiftInspectionResponse
	extends Omit<
		ShiftInspection,
		"InspectedAt" | "values" | "HasTolerances" | WeightFields
	> {
	InspectedAt: string;
}

type ShiftSpecItemResponse = {
	ProfileInspectionId: number;
} & InspectionToleranceSource["items"][number];

export const getInspectionsByShift = async (
	params: {
		assetId: number;
		shiftId?: number | null;
		profileInspectionId?: number | null;
		startDate?: Date | null;
		endDate?: Date | null;
		sectionId?: number | null;
		heatId?: number | null;
	},
	signal?: AbortSignal,
): Promise<ShiftInspectionsResult> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "AssetId", value: params.assetId },
		{ name: "ShiftId", value: params.shiftId ?? null },
		{ name: "ProfileInspectionId", value: params.profileInspectionId ?? null },
		{ name: "StartDate", value: params.startDate?.toISOString() ?? null },
		{ name: "EndDate", value: params.endDate?.toISOString() ?? null },
		{ name: "SectionId", value: params.sectionId ?? null },
		{ name: "HeatId", value: params.heatId ?? null },
	];

	const resp = await apiService.callV2(
		"[DIM].[GetProfileInspectionsByShift]",
		parameters,
		signal,
	);

	if (!resp.ok) throw new Error(resp.message || "Error fetching inspections");

	const headers = getTableRows<ShiftInspectionResponse>(resp, 0);
	const values = getTableRows<InspectionValue>(resp, 1);
	const shiftResponse = getTableRows<CurrentShiftMetadataResponse>(resp, 2)[0];
	const specItems = groupRowsByInspection(
		getTableRows<ShiftSpecItemResponse>(resp, 3),
	);
	// Without the spec items result set (SP not deployed yet) keep showing
	// "All in spec" instead of flagging every sample as "No Specification".
	const hasSpecItems = get(resp, "data.tables[3]") !== undefined;

	const byInspection = groupReadings(values);

	return {
		inspections: headers.map((header) => {
			const weight =
				byInspection.get(header.ProfileInspectionId)?.weight ?? NO_WEIGHT;
			return {
				...header,
				...weight,
				InspectedAt: moment.utc(header.InspectedAt).toDate(),
				values: byInspection.get(header.ProfileInspectionId)?.values ?? {},
				// Same inputs as the Report: every spec item, plus leg deltas on Mill #2 only.
				HasTolerances:
					!hasSpecItems ||
					hasInspectionTolerances({
						MinLbsPerFoot: weight.MinLbsPerFoot,
						MaxLbsPerFoot: weight.MaxLbsPerFoot,
						items: specItems.get(header.ProfileInspectionId) ?? [],
						legDeltas:
							header.AssetId === MILL_2.id
								? [{ LegDeltaMax: header.LegDeltaMax }]
								: [],
					}),
			};
		}),
		shift: shiftResponse
			? {
					ShiftId: shiftResponse.ShiftId,
					Start: moment.utc(shiftResponse.Start).toDate(),
					End: moment.utc(shiftResponse.End).toDate(),
				}
			: null,
	};
};

export const getInspections = async (
	params: {
		assetId: number;
	},
	signal?: AbortSignal,
): Promise<ProfileInspectionsResult> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "AssetId", value: params.assetId },
	];

	const resp = await apiService.callV2(
		"[DIM].[GetProfileInspections]",
		parameters,
		signal,
	);

	if (!resp.ok) throw new Error(resp.message || "Error fetching inspections");

	const headers = getTableRows<InspectionResponse>(resp, 0);
	const values = getTableRows<InspectionValue>(resp, 1);
	const currentShiftResponse = getTableRows<CurrentShiftMetadataResponse>(
		resp,
		2,
	)[0];
	const applicability = getTableRows<ProfileSpecApplicabilityResponse>(resp, 3);
	const applicableItemsBySpec: ProfileInspectionsResult["applicableItemsBySpec"] =
		{};
	for (const item of applicability) {
		// Older responses without item metadata must not mark every cell non-applicable.
		if (
			item.Code != null &&
			(item.ProfileItemId == null || item.SortOrder == null)
		)
			continue;
		const items = applicableItemsBySpec[item.ProfileSpecId] ?? [];
		if (
			item.ProfileItemId != null &&
			item.Code != null &&
			item.SortOrder != null
		) {
			items.push({
				ProfileItemId: item.ProfileItemId,
				Code: item.Code,
				SortOrder: item.SortOrder,
				Ltl: item.Ltl,
				Utl: item.Utl,
			});
		}
		applicableItemsBySpec[item.ProfileSpecId] = items;
	}
	if (!currentShiftResponse) {
		throw new Error("The inspections service did not return the current shift");
	}

	const byInspection = groupReadings(values);

	return {
		inspections: headers.map((h) => ({
			...h,
			...(byInspection.get(h.ProfileInspectionId)?.weight ?? NO_WEIGHT),
			InspectedAt: moment.utc(h.InspectedAt).toDate(),
			values: byInspection.get(h.ProfileInspectionId)?.values ?? {},
		})),
		currentShift: {
			ShiftId: currentShiftResponse.ShiftId,
			Start: moment.utc(currentShiftResponse.Start).toDate(),
			End: moment.utc(currentShiftResponse.End).toDate(),
		},
		applicableItemsBySpec,
	};
};

export const upsertInspection = async (params: UpsertInspectionPayload) => {
	const apiService = new MESApiService();

	if (params.measuredLbsPerFoot !== null && params.weightProfileItemId === null)
		throw new Error("The WEIGHT item is missing for this mill");
	const readings =
		params.measuredLbsPerFoot === null || params.weightProfileItemId === null
			? params.readings
			: [
					...params.readings,
					{
						ProfileItemId: params.weightProfileItemId,
						NumericValue: params.measuredLbsPerFoot,
					},
				];

	const parameters: spParameter[] = [
		{ name: "ProfileInspectionId", value: params.profileInspectionId },
		{ name: "AssetId", value: params.assetId },
		{ name: "SectionId", value: params.sectionId },
		{ name: "HeatId", value: params.heatId },
		{ name: "HeatName", value: params.heatName },
		{
			name: "InspectedAt",
			value: params.inspectedAt.toISOString(),
		},
		{ name: "Comments", value: params.comments },
		{ name: "ValuesJson", value: JSON.stringify(readings) },
	];

	const resp = await apiService.callV2(
		"[DIM].[UpsertProfileInspection]",
		parameters,
	);

	if (!resp.ok) throw new Error(resp.message || "Error saving the inspection");
	return resp;
};

export const deleteInspection = async (profileInspectionId: number) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ProfileInspectionId", value: profileInspectionId },
	];

	const resp = await apiService.callV2(
		"[DIM].[DeleteProfileInspection]",
		parameters,
	);

	if (!resp.ok)
		throw new Error(resp.message || "Error deleting the inspection");
	return resp;
};
